import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tabs,
  Tab,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  LinearProgress,
  Snackbar,
  Backdrop,
  Toolbar,
  AppBar,
  Avatar,
  Menu,
  Breadcrumbs,
  Drawer,
  ListItemIcon,
  ListItemButton,
  Badge,
  Skeleton,
  Fade,
  Slide,
  alpha,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  History as HistoryIcon,
  Check as CheckIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Schedule as ScheduleIcon,
  EventNote as EventNoteIcon,
  ArchiveOutlined as ArchiveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { useRole } from '../../contexts/RoleContext';

// Services - mantendo imports originais
import { registrarParecerRevisor, listarProjetos } from '../../services/projetoService';
import { listarMonografiasPorRevisor, listarMonografias, registrarParecerMonografia } from '../../services/monografiaService';
import api from '../../services/api';

// Componentes existentes
import DefesaList from '../../components/Defesa/DefesaList';
import ArquivarMonografia from '../../components/Monografia/ArquivarMonografia';

// ===== HOOKS PERSONALIZADOS PARA MOBILE =====
const useIsMobile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return { isMobile, isTablet, isSmallMobile };
};

const useSwipeGesture = (onSwipeLeft, onSwipeRight) => {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && onSwipeLeft) onSwipeLeft();
    if (isRightSwipe && onSwipeRight) onSwipeRight();
  };

  return { onTouchStart, onTouchMove, onTouchEnd };
};

const useDebounce = (callback, delay) => {
  const [debounceTimer, setDebounceTimer] = useState(null);

  const debouncedCallback = useCallback((...args) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    
    const timer = setTimeout(() => {
      callback(...args);
    }, delay);
    
    setDebounceTimer(timer);
  }, [callback, delay, debounceTimer]);

  useEffect(() => {
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [debounceTimer]);

  return debouncedCallback;
};

// ===== COMPONENTE DE NAVEGAÇÃO MOBILE =====
const MobileNavigation = ({ 
  activeTab, 
  onTabChange, 
  drawerOpen, 
  onDrawerToggle,
  notificationCount = 0,
  userName 
}) => {
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const menuItems = [
    { label: 'Dashboard', icon: <ScheduleIcon />, tab: 0 },
    { label: 'Projetos', icon: <AssignmentIcon />, tab: 1 },
    { label: 'Monografias', icon: <DescriptionIcon />, tab: 2 },
    { label: 'Histórico', icon: <HistoryIcon />, tab: 3 },
    { label: 'Defesas', icon: <EventNoteIcon />, tab: 4 },
    { label: 'Avaliações', icon: <VisibilityIcon />, tab: 5 },
    { label: 'Arquivamento', icon: <ArchiveIcon />, tab: 6 }
  ];

  const handleMenuClick = (tab) => {
    onTabChange(null, tab);
    onDrawerToggle(false);
  };

  return (
    <>
      {isMobile && (
        <AppBar position="sticky" sx={{ zIndex: 1300 }}>
          <Toolbar sx={{ minHeight: 64 }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => onDrawerToggle(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            
            <Typography variant="h6" sx={{ flexGrow: 1, fontSize: '1.1rem' }}>
              Revisor
            </Typography>

            <IconButton color="inherit">
              <Badge badgeContent={notificationCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            <IconButton 
              color="inherit"
              onClick={() => logout()}
              edge="end"
            >
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      <SwipeableDrawer
        anchor="left"
        open={drawerOpen}
        onClose={() => onDrawerToggle(false)}
        onOpen={() => onDrawerToggle(true)}
        disableBackdropTransition
        disableDiscovery
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {userName?.charAt(0).toUpperCase() || 'R'}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {userName || 'Revisor'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Revisor
              </Typography>
            </Box>
          </Box>
        </Box>

        <List sx={{ pt: 1 }}>
          {menuItems.map((item, index) => (
            <ListItemButton 
              key={index}
              selected={activeTab === item.tab}
              onClick={() => handleMenuClick(item.tab)}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.50',
                  '&:hover': {
                    bgcolor: 'primary.100',
                  }
                }
              }}
            >
              <ListItemIcon sx={{ color: activeTab === item.tab ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                sx={{ 
                  '& .MuiListItemText-primary': {
                    fontWeight: activeTab === item.tab ? 'bold' : 'normal',
                    color: activeTab === item.tab ? 'primary.main' : 'inherit'
                  }
                }}
              />
            </ListItemButton>
          ))}
        </List>

        <Box sx={{ mt: 'auto', p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={() => logout()}
            color="error"
          >
            Sair
          </Button>
        </Box>
      </SwipeableDrawer>
    </>
  );
};

MobileNavigation.propTypes = {
  activeTab: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
  drawerOpen: PropTypes.bool.isRequired,
  onDrawerToggle: PropTypes.func.isRequired,
  notificationCount: PropTypes.number,
  userName: PropTypes.string
};

// ===== COMPONENTE CARD EXPANSÍVEL =====
const ExpandableCard = ({ 
  children, 
  title, 
  subtitle, 
  status, 
  statusColor, 
  isNew, 
  actions,
  expanded = false,
  onExpand,
  ...props 
}) => {
  const { isMobile, isSmallMobile } = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(!isMobile || expanded);

  useEffect(() => {
    if (!isMobile) {
      setIsExpanded(true);
    } else {
      setIsExpanded(expanded);
    }
  }, [isMobile, expanded]);

  const handleExpand = () => {
    if (isMobile) {
      setIsExpanded(!isExpanded);
      if (onExpand) onExpand(!isExpanded);
    }
  };

  return (
    <Card 
      sx={{ 
        height: isMobile ? 'auto' : '100%',
        border: isNew ? '2px solid #2196f3' : '1px solid rgba(0,0,0,0.12)',
        boxShadow: isNew ? '0 4px 8px rgba(33, 150, 243, 0.3)' : undefined,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 6,
          transform: isMobile ? 'none' : 'translateY(-2px)'
        }
      }}
      {...props}
    >
      {isMobile ? (
        <Accordion expanded={isExpanded} onChange={handleExpand}>
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              minHeight: 64,
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                gap: 1
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant={isSmallMobile ? "subtitle2" : "h6"} 
                  sx={{ 
                    fontWeight: 'bold',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {title}
                </Typography>
                {subtitle && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isNew && (
                  <Chip
                    label="NOVO"
                    size="small"
                    color="primary"
                    sx={{ 
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                      height: 20
                    }}
                  />
                )}
                {status && (
                  <Chip
                    label={status}
                    color={statusColor}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {children}
            {actions && (
              <Box sx={{ 
                mt: 2, 
                pt: 2, 
                borderTop: 1, 
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}>
                {actions}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ) : (
        <>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {title}
                  </Typography>
                  {isNew && (
                    <Chip
                      label="NOVO"
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </Box>
                {status && (
                  <Chip
                    label={status}
                    color={statusColor}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                )}
              </Box>
            </Box>
            {children}
          </CardContent>
          {actions && (
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              {actions}
            </Box>
          )}
        </>
      )}
    </Card>
  );
};

ExpandableCard.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  status: PropTypes.string,
  statusColor: PropTypes.string,
  isNew: PropTypes.bool,
  actions: PropTypes.node,
  expanded: PropTypes.bool,
  onExpand: PropTypes.func
};

// ===== COMPONENTE HEADER MOBILE-OPTIMIZED =====
const DashboardHeader = ({ darkMode, setDarkMode, onBack, userName, onRefresh, refreshing }) => {
  const theme = useTheme();
  const { isMobile } = useIsMobile();
  const [anchorElProfile, setAnchorElProfile] = useState(null);
  const [anchorElNotifications, setAnchorElNotifications] = useState(null);
  const navigate = useNavigate();

  const handleProfileMenuOpen = (event) => {
    setAnchorElProfile(event.currentTarget);
  };

  const handleNotificationsOpen = (event) => {
    setAnchorElNotifications(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorElProfile(null);
    setAnchorElNotifications(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    navigate('/login');
  };

  if (isMobile) return null; // Header será substituído pela navegação mobile

  return (
    <AppBar 
      position="sticky" 
      sx={{ 
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        boxShadow: theme.shadows[4]
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: '72px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {onBack && (
            <IconButton
              color="inherit"
              onClick={onBack}
              sx={{
                bgcolor: alpha('#ffffff', 0.1),
                '&:hover': { bgcolor: alpha('#ffffff', 0.2) },
                transition: 'all 0.3s ease-in-out'
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
              Painel do Revisor
            </Typography>
            <Breadcrumbs 
              sx={{ color: alpha('#ffffff', 0.8) }}
              separator="›"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <HomeIcon fontSize="small" />
                <Typography variant="body2" color="inherit">Dashboard</Typography>
              </Box>
              <Typography variant="body2" color="inherit">Revisor</Typography>
            </Breadcrumbs>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            color="inherit"
            onClick={onRefresh}
            disabled={refreshing}
            sx={{
              bgcolor: alpha('#ffffff', 0.1),
              '&:hover': { bgcolor: alpha('#ffffff', 0.2) },
              transition: 'all 0.3s ease-in-out'
            }}
          >
            {refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          </IconButton>

          <IconButton
            color="inherit"
            onClick={() => setDarkMode(!darkMode)}
            sx={{
              bgcolor: alpha('#ffffff', 0.1),
              '&:hover': { bgcolor: alpha('#ffffff', 0.2) },
              transition: 'all 0.3s ease-in-out'
            }}
          >
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          <IconButton
            color="inherit"
            onClick={handleNotificationsOpen}
            sx={{
              bgcolor: alpha('#ffffff', 0.1),
              '&:hover': { bgcolor: alpha('#ffffff', 0.2) },
              transition: 'all 0.3s ease-in-out'
            }}
          >
            <Badge badgeContent={3} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            <Avatar 
              sx={{ 
                bgcolor: alpha('#ffffff', 0.2),
                color: 'white',
                width: 40,
                height: 40,
                fontWeight: 600
              }}
            >
              {userName?.charAt(0)?.toUpperCase() || 'R'}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {userName || 'Revisor'}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.8) }}>
                Revisor
              </Typography>
            </Box>
            <IconButton
              color="inherit"
              onClick={handleProfileMenuOpen}
              sx={{
                bgcolor: alpha('#ffffff', 0.1),
                '&:hover': { bgcolor: alpha('#ffffff', 0.2) },
                transition: 'all 0.3s ease-in-out'
              }}
            >
              <AccountCircleIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Menus */}
        <Menu
          anchorEl={anchorElProfile}
          open={Boolean(anchorElProfile)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              boxShadow: theme.shadows[8],
              borderRadius: 2,
              minWidth: 200
            }
          }}
        >
          <MenuItem onClick={handleMenuClose}>
            <ListItemIcon><AccountCircleIcon /></ListItemIcon>
            Meu Perfil
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <ListItemIcon><LogoutIcon color="error" /></ListItemIcon>
            Sair
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={anchorElNotifications}
          open={Boolean(anchorElNotifications)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              boxShadow: theme.shadows[8],
              borderRadius: 2,
              minWidth: 300,
              maxHeight: 400
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">Notificações</Typography>
          </Box>
          <MenuItem>
            <Typography variant="body2">Nova monografia para revisão</Typography>
          </MenuItem>
          <MenuItem>
            <Typography variant="body2">Projeto aprovado pela chefia</Typography>
          </MenuItem>
          <MenuItem>
            <Typography variant="body2">Defesa agendada para amanhã</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

DashboardHeader.propTypes = {
  darkMode: PropTypes.bool,
  setDarkMode: PropTypes.func,
  onBack: PropTypes.func,
  userName: PropTypes.string,
  onRefresh: PropTypes.func,
  refreshing: PropTypes.bool
};

// ===== COMPONENTE DE NOTIFICAÇÃO OTIMIZADO =====
const ToastNotification = ({ open, message, severity, onClose }) => {
  const { isMobile } = useIsMobile();
  
  const getIcon = () => {
    switch (severity) {
      case 'success': return <CheckCircleIcon />;
      case 'error': return <ErrorIcon />;
      case 'warning': return <WarningIcon />;
      case 'info': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={4000}
      onClose={onClose}
      anchorOrigin={{ 
        vertical: isMobile ? 'bottom' : 'top', 
        horizontal: isMobile ? 'center' : 'right' 
      }}
    >
      <Alert 
        onClose={onClose} 
        severity={severity} 
        sx={{ 
          width: '100%',
          minWidth: isMobile ? '300px' : '350px',
          fontSize: isMobile ? '16px' : '14px',
          borderRadius: 2,
          boxShadow: 8,
          '& .MuiAlert-icon': {
            fontSize: isMobile ? '24px' : '20px'
          }
        }}
        icon={getIcon()}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

ToastNotification.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  severity: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
  onClose: PropTypes.func.isRequired
};

// ===== LOADING SKELETON =====
const LoadingSkeleton = () => {
  const { isMobile } = useIsMobile();
  
  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Skeleton 
        variant="rectangular" 
        height={60} 
        sx={{ mb: 3, borderRadius: 2 }} 
      />
      <Grid container spacing={isMobile ? 2 : 3}>
        {[1,2,3].map(item => (
          <Grid item xs={12} md={4} key={item}>
            <Skeleton 
              variant="rectangular" 
              height={120} 
              sx={{ borderRadius: 2 }} 
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// ===== COMPONENTE PRINCIPAL =====
function RevisorDashboardPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMobile, isSmallMobile } = useIsMobile();

  const { currentRole } = useRole();
const effectiveRole = currentRole && currentRole !== 'auto' ? currentRole : (user?.tipo || '');

  // Estados principais
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Estados existentes do dashboard original
  const [projetosDesignados, setProjetosDesignados] = useState([]);
  const [projetosHistorico, setProjetosHistorico] = useState([]);
  const [parecerDialogOpen, setParecerDialogOpen] = useState(false);
  const [selectedProjeto, setSelectedProjeto] = useState(null);
  const [parecer, setParecer] = useState('');
  const [recomendacao, setRecomendacao] = useState('APROVAR');

  const [monografiasDesignadas, setMonografiasDesignadas] = useState([]);
  const [monografiasHistorico, setMonografiasHistorico] = useState([]);
  const [parecerDialogOpenMonografia, setParecerDialogOpenMonografia] = useState(false);
  const [selectedMonografia, setSelectedMonografia] = useState(null);
  const [parecerMonografia, setParecerMonografia] = useState('');
  const [recomendacaoMonografia, setRecomendacaoMonografia] = useState('APROVAR');

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [submittingParecer, setSubmittingParecer] = useState(false);
  const [submittingParecerMonografia, setSubmittingParecerMonografia] = useState(false);

  // Hooks personalizados
  const swipeGestures = useSwipeGesture(
    () => {
      if (drawerOpen) setDrawerOpen(false);
      else if (activeTab < 6) setActiveTab(activeTab + 1);
    },
    () => {
      if (!drawerOpen) setDrawerOpen(true);
      else if (activeTab > 0) setActiveTab(activeTab - 1);
    }
  );

  // Funções de notificação
  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  // Função principal para carregar dados (mantida do original)
  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      showNotification('Carregando dados do painel...', 'info');
      
      const responseProjetos = await listarProjetos();
      const projetosDoRevisor = responseProjetos.data.filter(p => p.revisor_id === user.id);
      const projetosEmAndamento = projetosDoRevisor.filter(p => p.status === 'EM_REVISAO');
      const projetosHistoricoData = projetosDoRevisor.filter(p => p.status !== 'EM_REVISAO');
      setProjetosDesignados(projetosEmAndamento);
      setProjetosHistorico(projetosHistoricoData);
      
      const responseMonografias = await listarMonografiasPorRevisor(user.id);
      const monografiasEmAndamento = responseMonografias.data.filter(m => m.status === 'EM_REVISAO');
      const monografiasHistoricoData = responseMonografias.data.filter(m => m.status !== 'EM_REVISAO');
      setMonografiasDesignadas(monografiasEmAndamento);
      setMonografiasHistorico(monografiasHistoricoData);
      
      setError(null);
      showNotification('Dados carregados com sucesso!', 'success');
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Não foi possível carregar os dados. Por favor, tente novamente.');
      showNotification('Erro ao carregar dados. Tente novamente.', 'error');
    } finally {
      setLoading(false);
    }
  }, [user?.id, showNotification]);

  const debouncedRefresh = useDebounce(async () => {
    setRefreshing(true);
    await carregarDados();
    setRefreshing(false);
  }, 300);

useEffect(() => {
  if (user?.id && effectiveRole === 'revisor_projeto') {
    carregarDados();
  }
}, [user?.id, effectiveRole, carregarDados]);

  // Funções de manipulação de parecer (mantidas do original)
  const openParecerDialog = useCallback((projeto) => {
    setSelectedProjeto(projeto);
    setParecer('');
    setRecomendacao('APROVAR');
    setParecerDialogOpen(true);
  }, []);

  const handleRegistrarParecer = useCallback(async () => {
    try {
      setSubmittingParecer(true);
      showNotification('Submetendo parecer do projeto...', 'info');
      
      await registrarParecerRevisor({
        projetoId: selectedProjeto.id,
        parecer,
        recomendacao
      });
      
      setParecerDialogOpen(false);
      showNotification(
        `Parecer do projeto "${selectedProjeto.proposta?.titulo || `#${selectedProjeto.id}`}" submetido com sucesso! Recomendação: ${recomendacao}`, 
        'success'
      );
      await carregarDados();
    } catch (err) {
      console.error('Erro ao registar parecer:', err);
      setError('Erro ao registar parecer. Por favor, tente novamente.');
      showNotification('Erro ao submeter parecer do projeto. Tente novamente.', 'error');
    } finally {
      setSubmittingParecer(false);
    }
  }, [selectedProjeto, parecer, recomendacao, showNotification, carregarDados]);

  // Funções para documentos (mantidas do original com melhorias mobile)
  const handleDownloadDocument = useCallback(async (documento, tipo) => {
    try {
      showNotification('Iniciando download do documento...', 'info');
      
      let fileUrl = null;
      let fileName = null;

      if (tipo === 'projeto') {
        fileUrl = documento.documento_projeto;
        fileName = `projeto_${documento.id}_${documento.proposta?.titulo?.substring(0, 20) || 'documento'}`;
      } else if (tipo === 'monografia') {
        fileUrl = documento.documento_monografia || documento.arquivo_url || documento.documento_url;
        fileName = `monografia_${documento.id}_${(documento.titulo || documento.projeto?.proposta?.titulo)?.substring(0, 20) || 'documento'}`;
      }

      if (!fileUrl) {
        showNotification('Documento não encontrado para download.', 'warning');
        return;
      }

      const fileNameFromPath = fileUrl.split('\\').pop().split('/').pop();
      const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.zenkroll.co.mz';
const apiUrl = `${baseUrl}/api/documentos/${fileNameFromPath}`;
      const token = localStorage.getItem('token');
      
      if (!token) {
        showNotification('Sessão expirada. Faça login novamente.', 'error');
        return;
      }

      showNotification('Processando download...', 'info');

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status} - ${response.statusText}`);
      }

      const blob = await response.blob();
      const originalExtension = fileNameFromPath.split('.').pop();
      const finalFileName = `${fileName}.${originalExtension}`;

      const objectUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = objectUrl;
      downloadLink.download = finalFileName;
      downloadLink.style.display = 'none';
      
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(objectUrl);
      }, 100);

      showNotification(`Download concluído: ${finalFileName}`, 'success');

    } catch (error) {
      console.error('Erro geral no download:', error);
      showNotification(`Erro ao baixar documento: ${error.message}`, 'error');
    }
  }, [showNotification]);

  const handleViewDocument = useCallback(async (documento, tipo) => {
    try {
      showNotification('Abrindo documento...', 'info');
      
      let fileUrl = null;
      
      if (tipo === 'projeto') {
        fileUrl = documento.documento_projeto;
      } else if (tipo === 'monografia') {
        fileUrl = documento.documento_monografia || documento.arquivo_url || documento.documento_url;
      }

      if (!fileUrl) {
        showNotification('Documento não disponível para visualização.', 'warning');
        return;
      }

    const fileNameFromPath = fileUrl.split('\\').pop().split('/').pop();
const fileExtension = fileNameFromPath.split('.').pop().toLowerCase();
const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.zenkroll.co.mz';
const publicUrl = `${baseUrl}/api/documentos/view/${fileNameFromPath}`;

if (fileExtension === 'pdf') {
  window.open(publicUrl, '_blank');
        showNotification('Documento PDF aberto em nova aba!', 'success');
      } else if (['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt'].includes(fileExtension)) {
        const userChoice = window.confirm(
          `Documento: ${fileNameFromPath}\n\n` +
          `Os documentos Word/Excel não podem ser visualizados diretamente no navegador.\n\n` +
          `Clique OK para baixar e abrir no seu computador.\n` +
          `Clique Cancelar para tentar visualizar online (pode não funcionar).`
        );
        
        if (userChoice) {
          handleDownloadDocument(documento, tipo);
        } else {
          const googleViewerUrl = `https://docs.google.com/gviewer?url=${encodeURIComponent(publicUrl)}&embedded=true`;
          window.open(googleViewerUrl, '_blank', 'width=1200,height=800');
          showNotification('Tentando visualizar documento online...', 'info');
        }
      } else {
        window.open(publicUrl, '_blank');
        showNotification('Documento aberto em nova aba!', 'success');
      }

    } catch (error) {
      console.error('Erro ao visualizar documento:', error);
      showNotification(`Erro ao abrir documento: ${error.message}`, 'error');
    }
  }, [showNotification, handleDownloadDocument]);

  // Componente de cards de métricas otimizado para mobile
  const MetricCard = ({ title, value, color, darkColor, count, status, icon: Icon, onClick }) => (
    <Fade in timeout={600}>
      <Card
        sx={{
          background: isMobile 
            ? `linear-gradient(135deg, ${color}20 0%, ${darkColor}30 100%)` 
            : `linear-gradient(135deg, ${color} 0%, ${darkColor} 100%)`,
          color: isMobile ? color : 'white',
          transition: 'all 0.3s ease-in-out',
          cursor: onClick ? 'pointer' : 'default',
          borderRadius: 3,
          height: isMobile ? 'auto' : '100%',
          border: isMobile ? `2px solid ${color}30` : 'none',
          '&:hover': { 
            transform: isMobile ? 'scale(1.02)' : 'translateY(-8px)',
            boxShadow: theme.shadows[8]
          }
        }}
        onClick={onClick}
      >
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            mb: isMobile ? 1 : 2,
            flexDirection: isMobile ? 'row' : 'row'
          }}>
            <Icon sx={{ 
              fontSize: isMobile ? 28 : 40, 
              opacity: 0.9,
              color: isMobile ? color : 'inherit'
            }} />
            <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 700 }}>
              {value}
            </Typography>
          </Box>
          
          <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ 
            mb: isMobile ? 1 : 2, 
            fontWeight: 500,
            fontSize: isMobile ? '0.95rem' : '1.125rem'
          }}>
            {title}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {count !== undefined && (
              <Chip 
                label={`${count} ${status}`} 
                sx={{ 
                  bgcolor: isMobile ? alpha(color, 0.2) : alpha('#ffffff', 0.2),
                  color: isMobile ? color : 'white',
                  borderRadius: 2,
                  fontWeight: 500,
                  fontSize: isMobile ? '0.7rem' : '0.75rem'
                }} 
                size="small"
              />
            )}
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );

  MetricCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    color: PropTypes.string.isRequired,
    darkColor: PropTypes.string.isRequired,
    count: PropTypes.number,
    status: PropTypes.string,
    icon: PropTypes.elementType.isRequired,
    onClick: PropTypes.func
  };

  // Componente de tabs otimizada para mobile
  const EnhancedTabs = ({ value, onChange, tabs }) => (
    <Box sx={{ mb: isMobile ? 2 : 4 }}>
      {isMobile ? (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={value}
            onChange={onChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                minWidth: 90,
                fontSize: '0.875rem',
                px: 1,
                minHeight: 48,
                textTransform: 'none',
                fontWeight: 500
              }
            }}
          >
            {tabs.map((tab, index) => (
              <Tab 
                key={index}
                icon={React.cloneElement(tab.icon, { fontSize: 'small' })}
                label={tab.label}
                iconPosition="top"
                sx={{
                  '&.Mui-selected': {
                    color: 'primary.main',
                    fontWeight: 'bold'
                  }
                }}
              />
            ))}
          </Tabs>
        </Box>
      ) : (
        <Tabs
          value={value}
          onChange={onChange}
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 64,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              margin: '0 4px',
              transition: 'all 0.3s ease-in-out',
              '&:hover': { 
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                transform: 'translateY(-2px)'
              },
              '&.Mui-selected': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main
              }
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab 
              key={index}
              icon={tab.icon} 
              label={tab.label}
              iconPosition="start"
            />
          ))}
        </Tabs>
      )}
    </Box>
  );

  EnhancedTabs.propTypes = {
    value: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    tabs: PropTypes.arrayOf(PropTypes.shape({
      icon: PropTypes.element.isRequired,
      label: PropTypes.string.isRequired
    })).isRequired
  };

  // Renderização do dashboard principal
  const renderDashboard = () => {
    const estatisticas = {
      projetos: {
        designados: projetosDesignados.length,
        concluidos: projetosHistorico.length,
        total: projetosDesignados.length + projetosHistorico.length
      },
      monografias: {
        designadas: monografiasDesignadas.length,
        concluidas: monografiasHistorico.length,
        total: monografiasDesignadas.length + monografiasHistorico.length
      }
    };

    return (
      <Fade in timeout={600}>
        <Box>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
            mb: isMobile ? 3 : 4, 
            fontWeight: 600,
            textAlign: isMobile ? 'center' : 'left'
          }}>
            Visão Geral
          </Typography>
          
          {isMobile ? (
            // Layout mobile com cards em coluna
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <MetricCard
                title="Projetos para Revisão"
                value={estatisticas.projetos.designados}
                color={theme.palette.primary.main}
                darkColor={theme.palette.primary.dark}
                count={estatisticas.projetos.total}
                status="Total"
                icon={AssignmentIcon}
                onClick={() => setActiveTab(1)}
              />
              <MetricCard
                title="Monografias para Revisão"
                value={estatisticas.monografias.designadas}
                color={theme.palette.secondary.main}
                darkColor={theme.palette.secondary.dark}
                count={estatisticas.monografias.total}
                status="Total"
                icon={DescriptionIcon}
                onClick={() => setActiveTab(2)}
              />
            </Box>
          ) : (
            // Layout desktop com grid
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <MetricCard
                  title="Projetos para Revisão"
                  value={estatisticas.projetos.designados}
                  color={theme.palette.primary.main}
                  darkColor={theme.palette.primary.dark}
                  count={estatisticas.projetos.total}
                  status="Total"
                  icon={AssignmentIcon}
                  onClick={() => setActiveTab(1)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MetricCard
                  title="Monografias para Revisão"
                  value={estatisticas.monografias.designadas}
                  color={theme.palette.secondary.main}
                  darkColor={theme.palette.secondary.dark}
                  count={estatisticas.monografias.total}
                  status="Total"
                  icon={DescriptionIcon}
                  onClick={() => setActiveTab(2)}
                />
              </Grid>
            </Grid>
          )}

          {/* Lista de itens pendentes otimizada para mobile */}
          {(projetosDesignados.length > 0 || monografiasDesignadas.length > 0) && (
            <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ 
                  mb: 3, 
                  color: 'primary.main', 
                  fontWeight: 600,
                  textAlign: isMobile ? 'center' : 'left'
                }}>
                  Itens Aguardando Sua Revisão
                </Typography>
                
                {projetosDesignados.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Projetos
                    </Typography>
                    <List>
                      {projetosDesignados.slice(0, isMobile ? 2 : 3).map((projeto, index) => (
                        <Slide in timeout={400 + (index * 100)} key={projeto.id}>
                          <ListItem 
                            sx={{ 
                              bgcolor: 'background.paper', 
                              mb: 1, 
                              borderRadius: 2,
                              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              transition: 'all 0.3s ease-in-out',
                              flexDirection: isMobile ? 'column' : 'row',
                              alignItems: isMobile ? 'stretch' : 'center',
                              gap: isMobile ? 1 : 0,
                              '&:hover': {
                                transform: isMobile ? 'none' : 'translateX(4px)',
                                borderColor: alpha(theme.palette.primary.main, 0.3),
                                boxShadow: theme.shadows[4]
                              }
                            }}
                          >
                            <ListItemText 
                              primary={projeto.proposta?.titulo || `Projeto #${projeto.id}`} 
                              secondary={`Estudante: ${projeto.proposta?.estudante?.nome || 'Não informado'}`}
                              sx={{
                                '& .MuiListItemText-primary': {
                                  fontSize: isMobile ? '0.95rem' : '1rem',
                                  fontWeight: isMobile ? 500 : 400
                                }
                              }}
                            />
                            <IconButton 
                              edge="end" 
                              onClick={() => navigate(`/projetos/${projeto.id}`)}
                              sx={{ 
                                borderRadius: 2,
                                alignSelf: isMobile ? 'center' : 'auto',
                                '&:hover': { 
                                  bgcolor: alpha(theme.palette.primary.main, 0.1) 
                                }
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </ListItem>
                        </Slide>
                      ))}
                    </List>
                    {projetosDesignados.length > (isMobile ? 2 : 3) && (
                      <Button 
                        variant="outlined" 
                        size={isMobile ? "medium" : "small"}
                        onClick={() => setActiveTab(1)} 
                        sx={{ mt: 2, borderRadius: 2 }}
                        fullWidth={isMobile}
                      >
                        Ver todos os {projetosDesignados.length} projetos
                      </Button>
                    )}
                  </Box>
                )}

                {monografiasDesignadas.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                      <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Monografias
                    </Typography>
                    <List>
                      {monografiasDesignadas.slice(0, isMobile ? 2 : 3).map((monografia, index) => (
                        <Slide in timeout={400 + (index * 100)} key={monografia.id}>
                          <ListItem 
                            sx={{ 
                              bgcolor: 'background.paper', 
                              mb: 1, 
                              borderRadius: 2,
                              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              transition: 'all 0.3s ease-in-out',
                              flexDirection: isMobile ? 'column' : 'row',
                              alignItems: isMobile ? 'stretch' : 'center',
                              gap: isMobile ? 1 : 0,
                              '&:hover': {
                                transform: isMobile ? 'none' : 'translateX(4px)',
                                borderColor: alpha(theme.palette.secondary.main, 0.3),
                                boxShadow: theme.shadows[4]
                              }
                            }}
                          >
                            <ListItemText 
                              primary={monografia.titulo || monografia.projeto?.proposta?.titulo || `Monografia #${monografia.id}`} 
                              secondary={`Estudante: ${monografia.projeto?.proposta?.estudante?.nome || 'Não informado'}`}
                              sx={{
                                '& .MuiListItemText-primary': {
                                  fontSize: isMobile ? '0.95rem' : '1rem',
                                  fontWeight: isMobile ? 500 : 400
                                }
                              }}
                            />
                            <IconButton 
                              edge="end" 
                              onClick={() => navigate(`/monografias/${monografia.id}`)}
                              sx={{ 
                                borderRadius: 2,
                                alignSelf: isMobile ? 'center' : 'auto',
                                '&:hover': { 
                                  bgcolor: alpha(theme.palette.secondary.main, 0.1) 
                                }
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </ListItem>
                        </Slide>
                      ))}
                    </List>
                    {monografiasDesignadas.length > (isMobile ? 2 : 3) && (
                      <Button 
                        variant="outlined" 
                        size={isMobile ? "medium" : "small"}
                        onClick={() => setActiveTab(2)} 
                        sx={{ mt: 2, borderRadius: 2 }}
                        fullWidth={isMobile}
                      >
                        Ver todas as {monografiasDesignadas.length} monografias
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
      </Fade>
    );
  };

  // Componente de item de documento otimizado para mobile
  const DocumentItem = ({ item, tipo, onView, onDownload, onDetails, onParecer }) => (
    <ExpandableCard
      title={tipo === 'projeto' 
        ? (item.proposta?.titulo || `Projeto #${item.id}`)
        : (item.titulo || item.projeto?.proposta?.titulo || `Monografia #${item.id}`)
      }
      subtitle={`Estudante: ${
        tipo === 'projeto' 
          ? (item.proposta?.estudante?.nome || 'Não informado')
          : (item.projeto?.proposta?.estudante?.nome || 'Não informado')
      }`}
      status={item.status}
      statusColor={item.status === 'EM_REVISAO' ? 'warning' : (item.status === 'APROVADO' || item.status === 'APROVADA' ? 'success' : 'info')}
      actions={
        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1 }}>
            <Button
              variant="outlined"
              color="info" 
              size={isMobile ? "medium" : "small"}
              startIcon={<VisibilityIcon />}
              onClick={() => onView(item, tipo)}
              disabled={tipo === 'projeto' ? !item.documento_projeto : (!item.arquivo_url && !item.documento_url && !item.documento_monografia)}
              sx={{ 
                borderRadius: 2,
                minWidth: isMobile ? 120 : 'auto'
              }}
            >
              Ver Documento
            </Button>
            
            <Button
              variant="outlined"
              color="secondary"
              size={isMobile ? "medium" : "small"}
              startIcon={<DownloadIcon />}
              onClick={() => onDownload(item, tipo)}
              disabled={tipo === 'projeto' ? !item.documento_projeto : (!item.arquivo_url && !item.documento_url && !item.documento_monografia)}
              sx={{ 
                borderRadius: 2,
                minWidth: isMobile ? 120 : 'auto'
              }}
            >
              Download
            </Button>
            
            <Button 
              variant="outlined" 
              size={isMobile ? "medium" : "small"}
              startIcon={<VisibilityIcon />} 
              onClick={() => onDetails(item.id)}
              sx={{ 
                borderRadius: 2,
                minWidth: isMobile ? 120 : 'auto'
              }}
            >
              Ver Detalhes
            </Button>
          </Box>

          {onParecer && (
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<EditIcon />} 
              onClick={() => onParecer(item)}
              sx={{ 
                borderRadius: 2, 
                fontWeight: 600,
                minWidth: isMobile ? 140 : 'auto'
              }}
              size={isMobile ? "medium" : "small"}
            >
              Emitir Parecer
            </Button>
          )}
        </Box>
      }
    >
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          <strong>Supervisor:</strong> {
            tipo === 'projeto'
              ? (item.supervisor?.Usuario?.nome || 'Não informado')
              : (item.projeto?.supervisor?.Usuario?.nome || 'Não informado')
          }
        </Typography>
        
        {tipo === 'projeto' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>Submetido em:</strong> {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'Não informado'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>Designado em:</strong> {item.data_encaminhamento ? new Date(item.data_encaminhamento).toLocaleDateString() : 'Não informado'}
            </Typography>
          </>
        )}
        
        {tipo === 'monografia' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>Submetida em:</strong> {item.data_submissao ? new Date(item.data_submissao).toLocaleDateString() : 'Não informado'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>Versão:</strong> {item.versao_documento || 'Não informado'}
            </Typography>
          </>
        )}
      </Box>
    </ExpandableCard>
  );

  DocumentItem.propTypes = {
    item: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      status: PropTypes.string,
      created_at: PropTypes.string,
      data_encaminhamento: PropTypes.string,
      data_submissao: PropTypes.string,
      versao_documento: PropTypes.string,
      documento_projeto: PropTypes.string,
      arquivo_url: PropTypes.string,
      documento_url: PropTypes.string,
      documento_monografia: PropTypes.string,
      titulo: PropTypes.string,
      proposta: PropTypes.shape({
        titulo: PropTypes.string,
        estudante: PropTypes.shape({
          nome: PropTypes.string
        })
      }),
      supervisor: PropTypes.shape({
        Usuario: PropTypes.shape({
          nome: PropTypes.string
        })
      }),
      projeto: PropTypes.shape({
        proposta: PropTypes.shape({
          titulo: PropTypes.string,
          estudante: PropTypes.shape({
            nome: PropTypes.string
          })
        }),
        supervisor: PropTypes.shape({
          Usuario: PropTypes.shape({
            nome: PropTypes.string
          })
        })
      })
    }).isRequired,
    tipo: PropTypes.oneOf(['projeto', 'monografia']).isRequired,
    onView: PropTypes.func.isRequired,
    onDownload: PropTypes.func.isRequired,
    onDetails: PropTypes.func.isRequired,
    onParecer: PropTypes.func
  };
  // Renderização das abas principais
  const renderProjetos = () => (
    <Fade in timeout={600}>
      <Box>
        <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
          <CardContent sx={{ p: isMobile ? 2 : 4 }}>
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
              mb: 3, 
              fontWeight: 600, 
              color: 'primary.main',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              Projetos Designados para Revisão
            </Typography>
            
            {projetosDesignados.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Não há projetos designados para sua revisão no momento.
              </Alert>
            ) : (
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3 }}>
                {projetosDesignados.map((projeto, index) => (
                  <DocumentItem
                    key={projeto.id}
                    item={projeto}
                    tipo="projeto"
                    onView={handleViewDocument}
                    onDownload={handleDownloadDocument}
                    onDetails={(id) => navigate(`/projetos/${id}`)}
                    onParecer={openParecerDialog}
                  />
                ))}
              </Box>
            )}
            
            <Divider sx={{ my: 4 }} />
            
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
              mb: 3, 
              fontWeight: 600, 
              color: 'primary.main',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              Histórico de Revisões
            </Typography>
            
            {projetosHistorico.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Não há histórico de revisões de projetos.
              </Alert>
            ) : (
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3 }}>
                {projetosHistorico.map((projeto, index) => (
                  <ExpandableCard
                    key={projeto.id}
                    title={projeto.proposta?.titulo || `Projeto #${projeto.id}`}
                    subtitle={`Estudante: ${projeto.proposta?.estudante?.nome || 'Não informado'}`}
                    status={projeto.status}
                    statusColor={projeto.status === 'APROVADO' ? 'success' : 'info'}
                    actions={
                      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1, width: '100%' }}>
                        <Button
                          variant="outlined"
                          color="info"
                          size={isMobile ? "medium" : "small"}
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDocument(projeto, 'projeto')}
                          disabled={!projeto.documento_projeto}
                          sx={{ borderRadius: 2, minWidth: isMobile ? 120 : 'auto' }}
                        >
                          Ver Documento
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size={isMobile ? "medium" : "small"}
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadDocument(projeto, 'projeto')}
                          disabled={!projeto.documento_projeto}
                          sx={{ borderRadius: 2, minWidth: isMobile ? 120 : 'auto' }}
                        >
                          Download
                        </Button>
                        <Button 
                          variant="outlined" 
                          size={isMobile ? "medium" : "small"}
                          startIcon={<VisibilityIcon />} 
                          onClick={() => navigate(`/projetos/${projeto.id}`)}
                          sx={{ borderRadius: 2, minWidth: isMobile ? 120 : 'auto' }}
                        >
                          Ver Detalhes
                        </Button>
                      </Box>
                    }
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>Revisor:</strong> {user?.nome || `Revisor #${user?.id}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Data da Revisão:</strong> {projeto.data_revisao ? new Date(projeto.data_revisao).toLocaleDateString() : 'Não informado'}
                      </Typography>
                      
                      {projeto.parecer_revisor && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                          <strong>Parecer:</strong> {projeto.parecer_revisor.substring(0, isMobile ? 100 : 150)}...
                        </Typography>
                      )}
                    </Box>
                  </ExpandableCard>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );

  const renderMonografias = () => (
    <Fade in timeout={600}>
      <Box>
        <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
          <CardContent sx={{ p: isMobile ? 2 : 4 }}>
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
              mb: 3, 
              fontWeight: 600, 
              color: 'secondary.main',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              Monografias Designadas para Revisão
            </Typography>
            
            {monografiasDesignadas.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Não há monografias designadas para sua revisão no momento.
              </Alert>
            ) : (
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3 }}>
                {monografiasDesignadas.map((monografia, index) => (
                  <DocumentItem
                    key={monografia.id}
                    item={monografia}
                    tipo="monografia"
                    onView={handleViewDocument}
                    onDownload={handleDownloadDocument}
                    onDetails={(id) => navigate(`/monografias/${id}`)}
                    onParecer={(monografia) => {
                      setSelectedMonografia(monografia);
                      setParecerMonografia('');
                      setRecomendacaoMonografia('APROVAR');
                      setParecerDialogOpenMonografia(true);
                    }}
                  />
                ))}
              </Box>
            )}
            
            <Divider sx={{ my: 4 }} />
            
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
              mb: 3, 
              fontWeight: 600, 
              color: 'secondary.main',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              Histórico de Revisões
            </Typography>
            
            {monografiasHistorico.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Não há histórico de revisões de monografias.
              </Alert>
            ) : (
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3 }}>
                {monografiasHistorico.map((monografia, index) => (
                  <ExpandableCard
                    key={monografia.id}
                    title={monografia.titulo || monografia.projeto?.proposta?.titulo || `Monografia #${monografia.id}`}
                    subtitle={`Estudante: ${monografia.projeto?.proposta?.estudante?.nome || 'Não informado'}`}
                    status={monografia.status}
                    statusColor={monografia.status === 'APROVADA' ? 'success' : 'info'}
                    actions={
                      <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1, width: '100%' }}>
                        <Button
                          variant="outlined"
                          color="info"
                          size={isMobile ? "medium" : "small"}
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDocument(monografia, 'monografia')}
                          disabled={!monografia.arquivo_url && !monografia.documento_url && !monografia.documento_monografia}
                          sx={{ borderRadius: 2, minWidth: isMobile ? 120 : 'auto' }}
                        >
                          Ver Documento
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size={isMobile ? "medium" : "small"}
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadDocument(monografia, 'monografia')}
                          disabled={!monografia.arquivo_url && !monografia.documento_url && !monografia.documento_monografia}
                          sx={{ borderRadius: 2, minWidth: isMobile ? 120 : 'auto' }}
                        >
                          Download
                        </Button>
                        <Button 
                          variant="outlined" 
                          size={isMobile ? "medium" : "small"}
                          startIcon={<VisibilityIcon />} 
                          onClick={() => navigate(`/monografias/${monografia.id}`)}
                          sx={{ borderRadius: 2, minWidth: isMobile ? 120 : 'auto' }}
                        >
                          Ver Detalhes
                        </Button>
                      </Box>
                    }
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>Supervisor:</strong> {monografia.projeto?.supervisor?.Usuario?.nome || 'Não informado'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Revista em:</strong> {monografia.data_revisao ? new Date(monografia.data_revisao).toLocaleDateString() : 'Não informado'}
                      </Typography>
                      
                      {monografia.parecer_revisor && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                          <strong>Parecer:</strong> {monografia.parecer_revisor.substring(0, isMobile ? 100 : 150)}...
                        </Typography>
                      )}
                    </Box>
                  </ExpandableCard>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );

  const renderDefesas = () => (
    <Fade in timeout={600}>
      <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
            mb: 3, 
            fontWeight: 600, 
            color: 'primary.main',
            textAlign: isMobile ? 'center' : 'left'
          }}>
            Defesas Agendadas
          </Typography>
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            Aqui você pode visualizar as defesas em que participa e acompanhar seu status.
          </Alert>
          <DefesaList />
        </CardContent>
      </Card>
    </Fade>
  );

  const RenderAvaliacoes = ({ showNotification: showNotif }) => {
    const [loadingAval, setLoadingAval] = useState(true);
    
    useEffect(() => {
      setTimeout(() => setLoadingAval(false), 1000);
    }, []);

    if (loadingAval) return <LoadingSkeleton />;

    return (
      <Fade in timeout={600}>
        <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
          <CardContent sx={{ p: isMobile ? 2 : 4 }}>
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
              mb: 3, 
              fontWeight: 600, 
              color: 'warning.main',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              Avaliações de Defesas
            </Typography>
            
            <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Total de Avaliações"
                  value="12"
                  color={theme.palette.info.main}
                  darkColor={theme.palette.info.dark}
                  icon={AssignmentIcon}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Avaliações Concluídas"
                  value="8"
                  color={theme.palette.success.main}
                  darkColor={theme.palette.success.dark}
                  icon={CheckCircleIcon}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Média Geral"
                  value="15.2"
                  color={theme.palette.warning.main}
                  darkColor={theme.palette.warning.dark}
                  icon={InfoIcon}
                />
              </Grid>
            </Grid>

            <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ mb: 2, fontWeight: 600 }}>
              Distribuição das Notas
            </Typography>
            
            {['Excelente', 'Muito Bom', 'Bom', 'Suficiente'].map((faixa, index) => (
              <Box key={faixa} sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                  {faixa}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={[30, 40, 20, 10][index]}
                  sx={{ 
                    height: isMobile ? 8 : 10, 
                    borderRadius: 5,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '& .MuiLinearProgress-bar': { 
                      borderRadius: 5,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {[3, 4, 2, 1][index]} avaliações
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Fade>
    );
  };

  RenderAvaliacoes.propTypes = {
    showNotification: PropTypes.func.isRequired
  };

  const renderAvaliacoes = (showNotif) => <RenderAvaliacoes showNotification={showNotif} />;

  const ArquivamentoTab = ({ user: userProp, setActiveTab: setActiveTabProp, showNotification: showNotif }) => {
    return (
      <Fade in timeout={600}>
        <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
          <CardContent sx={{ p: isMobile ? 2 : 4 }}>
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
              mb: 3, 
              fontWeight: 600, 
              color: 'secondary.main',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              Arquivamento de Monografias
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mb: 3,
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <Button
                variant="contained"
                size={isMobile ? "medium" : "small"}
                sx={{ borderRadius: 2 }}
                fullWidth={isMobile}
              >
                Pendentes de Arquivamento (3)
              </Button>
              <Button
                variant="outlined"
                size={isMobile ? "medium" : "small"}
                sx={{ borderRadius: 2 }}
                fullWidth={isMobile}
              >
                Arquivadas (15)
              </Button>
            </Box>
            
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Monografias aprovadas que você revisou e podem ser arquivadas no repositório digital.
            </Alert>
            
            <ArquivarMonografia 
              monografia={{}} 
              onSuccess={() => showNotif('Monografia arquivada com sucesso!', 'success')}
            />
          </CardContent>
        </Card>
      </Fade>
    );
  };

  ArquivamentoTab.propTypes = {
    user: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      nome: PropTypes.string
    }).isRequired,
    setActiveTab: PropTypes.func.isRequired,
    showNotification: PropTypes.func.isRequired
  };

  const renderHistorico = () => (
    <Fade in timeout={600}>
      <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
            mb: 3, 
            fontWeight: 600, 
            color: 'info.main',
            textAlign: isMobile ? 'center' : 'left'
          }}>
            Histórico de Revisões
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            Esta aba apresenta um histórico consolidado de todas as suas revisões de projetos e monografias.
          </Alert>
          
          <Box sx={{ mt: 4 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} color="primary" gutterBottom sx={{ fontWeight: 600 }}>
              <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Projetos Revistos ({projetosHistorico.length})
            </Typography>
            
            {projetosHistorico.length > 0 ? (
              projetosHistorico.slice(0, 3).map((projeto, index) => (
                <Slide in timeout={400 + (index * 100)} key={projeto.id}>
                  <Paper 
                    elevation={1}
                    sx={{ 
                      p: isMobile ? 2 : 3, 
                      mb: 2, 
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: isMobile ? 'none' : 'translateY(-2px)',
                        boxShadow: theme.shadows[4]
                      }
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={8}>
                        <Typography variant={isMobile ? "subtitle2" : "subtitle1"} sx={{ fontWeight: 600 }}>
                          {projeto.proposta?.titulo}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Estudante: {projeto.proposta?.estudante?.nome}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Revisto em: {projeto.data_revisao ? new Date(projeto.data_revisao).toLocaleDateString() : 'Não informado'}
                        </Typography>
                        <Chip 
                          label={projeto.status} 
                          color={projeto.status === 'APROVADO' ? 'success' : 'info'} 
                          size="small" 
                          sx={{ mt: 1, borderRadius: 2 }} 
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          flexDirection: isMobile ? 'row' : 'column',
                          justifyContent: isMobile ? 'center' : 'flex-start'
                        }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDocument(projeto, 'projeto')}
                            disabled={!projeto.documento_projeto}
                            sx={{ borderRadius: 2, minWidth: isMobile ? 100 : 'auto' }}
                          >
                            Ver Doc
                          </Button>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => navigate(`/projetos/${projeto.id}`)}
                            sx={{ borderRadius: 2, minWidth: isMobile ? 100 : 'auto' }}
                          >
                            Detalhes
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Slide>
              ))
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Não há projetos revistos no histórico.
              </Alert>
            )}
          </Box>
          
          <Box sx={{ mt: 4 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} color="secondary" gutterBottom sx={{ fontWeight: 600 }}>
              <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Monografias Revistas ({monografiasHistorico.length})
            </Typography>
            
            {monografiasHistorico.length > 0 ? (
              monografiasHistorico.slice(0, 3).map((monografia, index) => (
                <Slide in timeout={400 + (index * 100)} key={monografia.id}>
                  <Paper 
                    elevation={1}
                    sx={{ 
                      p: isMobile ? 2 : 3, 
                      mb: 2, 
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: isMobile ? 'none' : 'translateY(-2px)',
                        boxShadow: theme.shadows[4]
                      }
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={8}>
                        <Typography variant={isMobile ? "subtitle2" : "subtitle1"} sx={{ fontWeight: 600 }}>
                          {monografia.titulo || monografia.projeto?.proposta?.titulo || `Monografia #${monografia.id}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Estudante: {monografia.projeto?.proposta?.estudante?.nome || 'Não informado'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Revista em: {monografia.data_revisao ? new Date(monografia.data_revisao).toLocaleDateString() : 'Não informado'}
                        </Typography>
                        <Chip 
                          label={monografia.status} 
                          color={monografia.status === 'APROVADA' ? 'success' : 'info'} 
                          size="small" 
                          sx={{ mt: 1, borderRadius: 2 }} 
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          flexDirection: isMobile ? 'row' : 'column',
                          justifyContent: isMobile ? 'center' : 'flex-start'
                        }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewDocument(monografia, 'monografia')}
                            disabled={!monografia.arquivo_url && !monografia.documento_url && !monografia.documento_monografia}
                            sx={{ borderRadius: 2, minWidth: isMobile ? 100 : 'auto' }}
                          >
                            Ver Doc
                          </Button>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => navigate(`/monografias/${monografia.id}`)}
                            sx={{ borderRadius: 2, minWidth: isMobile ? 100 : 'auto' }}
                          >
                            Detalhes
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Slide>
              ))
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                Não há monografias revistas no histórico.
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: isMobile ? '50vh' : '400px',
        p: isMobile ? 2 : 3
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={isMobile ? 60 : 50} />
          <Typography variant={isMobile ? "h6" : "h6"} sx={{ mt: 2 }}>
            Carregando painel do revisor...
          </Typography>
        </Box>
      </Box>
    );
  }

  // JSX principal
  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: darkMode ? 'grey.900' : alpha(theme.palette.primary.main, 0.02),
      transition: 'background-color 0.3s ease-in-out',
      pb: isMobile ? 10 : 0
    }}
    {...swipeGestures}
    >
      {/* Navegação Mobile */}
      <MobileNavigation
        activeTab={activeTab}
        onTabChange={setActiveTab}
        drawerOpen={drawerOpen}
        onDrawerToggle={setDrawerOpen}
        notificationCount={3}
        userName={user?.nome}
      />

      {/* Header Desktop */}
      <DashboardHeader
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        userName={user?.nome}
        onRefresh={debouncedRefresh}
        refreshing={refreshing}
      />

      {/* Container Principal */}
      <Box sx={{ maxWidth: isMobile ? '100%' : 1400, mx: 'auto', p: isMobile ? 1 : 4 }}>
        {/* Tabs */}
        <EnhancedTabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          tabs={[
            { 
              icon: <ScheduleIcon />, 
              label: isMobile ? 'Dashboard' : `Dashboard${projetosDesignados.length + monografiasDesignadas.length > 0 ? ` (${projetosDesignados.length + monografiasDesignadas.length})` : ''}` 
            },
            { 
              icon: <AssignmentIcon />, 
              label: isMobile ? 'Projetos' : `Projetos${projetosDesignados.length > 0 ? ` (${projetosDesignados.length})` : ''}` 
            },
            { 
              icon: <DescriptionIcon />, 
              label: isMobile ? 'Monografias' : `Monografias${monografiasDesignadas.length > 0 ? ` (${monografiasDesignadas.length})` : ''}` 
            },
            { 
              icon: <HistoryIcon />, 
              label: isMobile ? 'Histórico' : `Histórico${projetosHistorico.length + monografiasHistorico.length > 0 ? ` (${projetosHistorico.length + monografiasHistorico.length})` : ''}` 
            },
            { icon: <EventNoteIcon />, label: "Defesas" },
            { icon: <VisibilityIcon />, label: "Avaliações" },
            { icon: <ArchiveIcon />, label: "Arquivamento" }
          ]}
        />

        {/* Alert de Erro */}
        {error && (
          <Fade in>
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                boxShadow: theme.shadows[4]
              }} 
              action={
                <Button 
                  color="inherit" 
                  size={isMobile ? "medium" : "small"}
                  onClick={carregarDados}
                  sx={{ borderRadius: 2 }}
                >
                  Tentar Novamente
                </Button>
              }
            >
              {error}
            </Alert>
          </Fade>
        )}

        {/* Speed Dial para mobile */}
        {isMobile && (
          <SpeedDial
            ariaLabel="Ações rápidas"
            sx={{ 
              position: 'fixed', 
              bottom: 80, 
              right: 16,
              zIndex: 1000
            }}
            icon={<SpeedDialIcon />}
            direction="up"
          >
            <SpeedDialAction
              icon={<RefreshIcon />}
              tooltipTitle="Atualizar"
              onClick={debouncedRefresh}
            />
            <SpeedDialAction
              icon={<NotificationsIcon />}
              tooltipTitle="Notificações"
              onClick={() => {}}
            />
            <SpeedDialAction
              icon={<FilterIcon />}
              tooltipTitle="Filtros"
              onClick={() => {}}
            />
          </SpeedDial>
        )}

        {/* Conteúdo das Abas */}
        <Fade in timeout={600}>
          <Box>
            {activeTab === 0 && renderDashboard()}
            {activeTab === 1 && renderProjetos()}
            {activeTab === 2 && renderMonografias()}
            {activeTab === 3 && renderHistorico()}
          {activeTab === 4 && renderDefesas()}
            {activeTab === 5 && renderAvaliacoes(showNotification)}
            {activeTab === 6 && <ArquivamentoTab user={user} setActiveTab={setActiveTab} showNotification={showNotification} />}
          </Box>
        </Fade>
      </Box>

      {/* Backdrop para Loading de Operações */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: 'blur(4px)'
        }}
        open={submittingParecer || submittingParecerMonografia}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: isMobile ? 3 : 3,
          p: isMobile ? 3 : 4,
          bgcolor: alpha('#000000', 0.8),
          borderRadius: 3,
          backdropFilter: 'blur(10px)'
        }}>
          <CircularProgress color="inherit" size={isMobile ? 60 : 60} />
          <Typography variant={isMobile ? "h6" : "h6"} sx={{ fontWeight: 600 }}>
            {submittingParecer ? 'Submetendo parecer do projeto...' : 'Submetendo parecer da monografia...'}
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 400, opacity: 0.8 }}>
            Por favor, aguarde enquanto processamos sua solicitação. Isso pode levar alguns segundos.
          </Typography>
        </Box>
      </Backdrop>

      {/* Sistema de Notificações */}
      <ToastNotification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
      />

      {/* Diálogo de Registro de Parecer para Projetos - Mobile Optimized */}
      <Dialog 
        open={parecerDialogOpen} 
        onClose={() => setParecerDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            boxShadow: theme.shadows[24],
            ...(isMobile && {
              m: 0,
              width: '100%',
              height: '100%'
            })
          }
        }}
      >
        <DialogTitle sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          p: isMobile ? 2 : 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EditIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
            <Box>
              <Typography variant={isMobile ? "h6" : "h6"} sx={{ fontWeight: 700 }}>
                Emitir Parecer para o Projecto
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Análise técnica detalhada do projeto de pesquisa
              </Typography>
            </Box>
          </Box>
          {isMobile && (
            <IconButton onClick={() => setParecerDialogOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ p: isMobile ? 2 : 4, flex: 1, overflow: 'auto' }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: isMobile ? 2 : 3, 
              mb: 3, 
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
              {selectedProjeto?.proposta?.titulo || `Projeto #${selectedProjeto?.id}`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>Estudante:</strong> {selectedProjeto?.proposta?.estudante?.nome || 'Não informado'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Supervisor:</strong> {selectedProjeto?.supervisor?.Usuario?.nome || 'Não informado'}
            </Typography>
          </Paper>
          
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Instruções:</strong> Seja específico em suas observações e recomendações. 
              Este parecer será revisto pelo Chefe do Departamento e disponibilizado ao estudante e supervisor.
            </Typography>
          </Alert>

          <TextField
            autoFocus={!isMobile}
            margin="dense"
            label="Parecer Técnico"
            fullWidth
            multiline
            rows={isMobile ? 6 : 8}
            value={parecer}
            onChange={(e) => setParecer(e.target.value)}
            placeholder="Descreva aqui sua análise técnica detalhada do projeto, incluindo pontos fortes, pontos a melhorar e recomendações específicas..."
            helperText={`${parecer.length}/2000 caracteres`}
            inputProps={{ maxLength: 2000 }}
            sx={{ 
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              },
              '& .MuiInputBase-input': {
                fontSize: isMobile ? '16px' : '14px'
              }
            }}
            required
            error={!parecer.trim()}
          />
          
          <FormControl fullWidth required>
            <InputLabel id="recomendacao-label">Recomendação Final</InputLabel>
            <Select
              labelId="recomendacao-label"
              value={recomendacao}
              onChange={(e) => setRecomendacao(e.target.value)}
              label="Recomendação Final"
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="APROVAR">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Aprovar Projeto</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Projeto aprovado sem restrições
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="APROVAR_COM_RESSALVAS">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="warning" fontSize="small" />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Aprovar com Ressalvas</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Aprovado com observações a considerar
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="REVER">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon color="info" fontSize="small" />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Solicitar Revisão</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Requer alterações antes da aprovação
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="REJEITAR">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon color="error" fontSize="small" />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Rejeitar Projeto</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Projeto não atende aos critérios
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: isMobile ? 2 : 3, 
          gap: isMobile ? 1 : 2, 
          bgcolor: alpha(theme.palette.background.paper, 0.5),
          borderTop: isMobile ? '1px solid' : 'none',
          borderColor: 'divider',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button 
            onClick={() => setParecerDialogOpen(false)}
            disabled={submittingParecer}
            sx={{ borderRadius: 2, minWidth: isMobile ? '100%' : 120 }}
            size={isMobile ? "large" : "medium"}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleRegistrarParecer} 
            color="primary" 
            variant="contained" 
            disabled={!parecer.trim() || submittingParecer}
            startIcon={submittingParecer ? <CircularProgress size={16} /> : <CheckIcon />}
            sx={{ borderRadius: 2, fontWeight: 600, minWidth: isMobile ? '100%' : 150 }}
            size={isMobile ? "large" : "medium"}
          >
            {submittingParecer ? 'Submetendo...' : 'Submeter Parecer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Registro de Parecer para Monografias - Mobile Optimized */}
      <Dialog 
        open={parecerDialogOpenMonografia} 
        onClose={() => setParecerDialogOpenMonografia(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            boxShadow: theme.shadows[24],
            ...(isMobile && {
              m: 0,
              width: '100%',
              height: '100%'
            })
          }
        }}
      >
        <DialogTitle sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
          color: 'white',
          p: isMobile ? 2 : 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EditIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
            <Box>
              <Typography variant={isMobile ? "h6" : "h6"} sx={{ fontWeight: 700 }}>
                Emitir Parecer para a Monografia
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Avaliação completa do trabalho de conclusão
              </Typography>
            </Box>
          </Box>
          {isMobile && (
            <IconButton onClick={() => setParecerDialogOpenMonografia(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ p: isMobile ? 2 : 4, flex: 1, overflow: 'auto' }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: isMobile ? 2 : 3, 
              mb: 3, 
              bgcolor: alpha(theme.palette.secondary.main, 0.05),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
            }}
          >
            <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ color: 'secondary.main', fontWeight: 600, mb: 1 }}>
              {selectedMonografia?.titulo || selectedMonografia?.projeto?.proposta?.titulo || `Monografia #${selectedMonografia?.id}`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>Estudante:</strong> {selectedMonografia?.projeto?.proposta?.estudante?.nome || 'Não informado'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Supervisor:</strong> {selectedMonografia?.projeto?.supervisor?.Usuario?.nome || 'Não informado'}
            </Typography>
          </Paper>

          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Instruções:</strong> Avalie cuidadosamente o conteúdo, metodologia, resultados e conclusões da monografia. 
              Seu parecer é fundamental para a decisão final sobre a aprovação.
            </Typography>
          </Alert>

          <TextField
            autoFocus={!isMobile}
            margin="dense"
            label="Parecer Técnico"
            fullWidth
            multiline
            rows={isMobile ? 6 : 8}
            value={parecerMonografia}
            onChange={(e) => setParecerMonografia(e.target.value)}
            placeholder="Descreva aqui sua análise detalhada da monografia, incluindo avaliação da metodologia, qualidade dos resultados, contribuição científica e recomendações..."
            helperText={`${parecerMonografia.length}/2000 caracteres`}
            inputProps={{ maxLength: 2000 }}
            sx={{ 
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              },
              '& .MuiInputBase-input': {
                fontSize: isMobile ? '16px' : '14px'
              }
            }}
            required
            error={!parecerMonografia.trim()}
          />
          
          <FormControl fullWidth required>
            <InputLabel id="recomendacao-monografia-label">Recomendação Final</InputLabel>
            <Select
              labelId="recomendacao-monografia-label"
              value={recomendacaoMonografia}
              onChange={(e) => setRecomendacaoMonografia(e.target.value)}
              label="Recomendação Final"
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="APROVAR">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" fontSize="small" />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Aprovar Monografia</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Trabalho aprovado para defesa
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="APROVAR_COM_RESSALVAS">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="warning" fontSize="small" />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Aprovar com Ressalvas</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Aprovado com observações importantes
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="REVER">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon color="info" fontSize="small" />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Solicitar Revisão</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Requer correções substanciais
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="REJEITAR">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon color="error" fontSize="small" />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Rejeitar Monografia</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Trabalho não atende aos padrões
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: isMobile ? 2 : 3, 
          gap: isMobile ? 1 : 2, 
          bgcolor: alpha(theme.palette.background.paper, 0.5),
          borderTop: isMobile ? '1px solid' : 'none',
          borderColor: 'divider',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button 
            onClick={() => setParecerDialogOpenMonografia(false)}
            disabled={submittingParecerMonografia}
            sx={{ borderRadius: 2, minWidth: isMobile ? '100%' : 120 }}
            size={isMobile ? "large" : "medium"}
          >
            Cancelar
          </Button>
          <Button 
            onClick={async () => {
              try {
                setSubmittingParecerMonografia(true);
                showNotification('Submetendo parecer da monografia...', 'info');
                
                await registrarParecerMonografia(
                  selectedMonografia.id,
                  parecerMonografia,
                  recomendacaoMonografia
                );
                
                setParecerDialogOpenMonografia(false);
                showNotification(
                  `Parecer da monografia "${selectedMonografia.titulo || selectedMonografia.projeto?.proposta?.titulo || `#${selectedMonografia.id}`}" submetido com sucesso! Recomendação: ${recomendacaoMonografia}`, 
                  'success'
                );
                await carregarDados();
              } catch (err) {
                console.error('Erro ao registrar parecer da monografia:', err);
                setError('Erro ao registrar parecer. Por favor, tente novamente.');
                showNotification('Erro ao submeter parecer da monografia. Tente novamente.', 'error');
              } finally {
                setSubmittingParecerMonografia(false);
              }
            }} 
            color="secondary" 
            variant="contained" 
            disabled={!parecerMonografia.trim() || submittingParecerMonografia}
            startIcon={submittingParecerMonografia ? <CircularProgress size={16} /> : <CheckIcon />}
            sx={{ borderRadius: 2, fontWeight: 600, minWidth: isMobile ? '100%' : 150 }}
            size={isMobile ? "large" : "medium"}
          >
            {submittingParecerMonografia ? 'Submetendo...' : 'Submeter Parecer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RevisorDashboardPage;